import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Video, Users, ShieldCheck, RefreshCw, XCircle, Clock, Radio, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function ConferenzePage() {
  const [conf, setConf] = useState([]);
  const [disponibile, setDisponibile] = useState(true);
  const [errore, setErrore] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [durata, setDurata] = useState(0);
  const [opts, setOpts] = useState(null);
  const [ending, setEnding] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/conferenze");
      setDisponibile(data.disponibile); setErrore(data.errore || "");
      setConf(data.conferenze || []);
    } catch (e) { console.error(e); toast.error("Errore caricamento conferenze"); }
    finally { setLoading(false); }
  };
  const loadOpts = async () => {
    try { const { data } = await api.get("/settings/bbb"); setOpts(data.opzioni || {}); setDurata(data.opzioni?.duration || 0); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { load(); loadOpts(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const endOne = async (id) => {
    if (!window.confirm("Chiudere questa conferenza per tutti i partecipanti?")) return;
    setEnding(id);
    try { await api.post(`/admin/conferenze/${encodeURIComponent(id)}/end`); toast.success("Conferenza chiusa"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Chiusura non riuscita"); }
    finally { setEnding(""); }
  };
  const endAll = async () => {
    if (!window.confirm(`Chiudere TUTTE le ${conf.length} conferenze attive?`)) return;
    setEnding("ALL");
    try { const { data } = await api.post("/admin/conferenze/end-all"); toast.success(`Chiuse ${data.chiuse}/${data.totali} conferenze`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
    finally { setEnding(""); }
  };
  const saveDurata = async () => {
    try {
      await api.put("/settings/bbb", { opzioni: { ...(opts || {}), duration: durata === "" ? 0 : Number(durata) } });
      toast.success("Durata predefinita salvata (vale per le nuove conferenze)");
    } catch { toast.error("Errore salvataggio durata"); }
  };

  const elapsed = (ts) => ts ? `${Math.max(0, Math.floor((Date.now() - ts) / 60000))} min` : "—";

  return (
    <div className="p-6 space-y-6" data-testid="conferenze-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 flex items-center gap-2"><Video className="w-7 h-7 text-[#1F4E79]"/> Conferenze BBB</h1>
          <p className="text-stone-600 text-sm">Controllo delle aule virtuali attive in tempo reale</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="conf-refresh-btn"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`}/>Aggiorna</Button>
          <Button onClick={endAll} disabled={conf.length === 0 || ending === "ALL"} className="bg-red-600 hover:bg-red-700 text-white" data-testid="conf-end-all-btn"><XCircle className="w-4 h-4 mr-1"/>Chiudi tutte</Button>
        </div>
      </div>

      {!disponibile && (
        <Card className="p-4 bg-amber-50 border-amber-200 text-amber-800 text-sm" data-testid="conf-unavailable">
          Server BBB non raggiungibile{errore ? `: ${errore}` : ". Verifica la configurazione in Impostazioni."}
        </Card>
      )}

      <Card className="p-5 bg-white border-[#E2E8F0]">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="flex items-center gap-1"><Clock className="w-3.5 h-3.5"/> Durata max predefinita nuove conferenze (min)</Label>
            <Input type="number" min="0" value={durata} onChange={e=>setDurata(e.target.value === "" ? "" : Number(e.target.value))} className="max-w-[200px]" data-testid="conf-durata-input"/>
          </div>
          <Button onClick={saveDurata} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="conf-durata-save-btn">Salva durata</Button>
        </div>
        <div className="text-xs text-stone-500 mt-2">0 = nessun limite (la conferenza resta aperta finché la chiude il moderatore). Nota: BBB non consente di cambiare la durata di una conferenza <b>già avviata</b>: il valore vale per le nuove aule.</div>
      </Card>

      {conf.length === 0 ? (
        <Card className="p-10 bg-white border-[#E2E8F0] text-center text-stone-500" data-testid="conf-empty">Nessuna conferenza attiva al momento.</Card>
      ) : conf.map(c => {
        const mods = (c.attendees || []).filter(a => a.ruolo === "MODERATOR");
        const studs = (c.attendees || []).filter(a => a.ruolo !== "MODERATOR");
        const isOpen = !!expanded[c.meeting_id];
        return (
          <Card key={c.meeting_id} className="p-5 bg-white border-[#E2E8F0]" data-testid={`conf-card-${c.meeting_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${c.running ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-stone-100 text-stone-500"}`}>
                    <Radio className={`w-3 h-3 ${c.running ? "text-emerald-500" : ""}`}/> {c.running ? "In corso" : "In avvio"}
                  </span>
                  <span className="font-semibold text-stone-900 truncate">{c.nome || c.meeting_id}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-stone-600">
                  <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5"/> {c.partecipanti} partecipanti</span>
                  <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-[#1F4E79]"/> {c.moderatori} moderatori</span>
                  <span className="inline-flex items-center gap-1"><Video className="w-3.5 h-3.5"/> {c.video} webcam</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5"/> attiva da {elapsed(c.creata_ts)}</span>
                  <span>capienza {c.max_utenti} · durata max {c.durata_max_min ? `${c.durata_max_min} min` : "illimitata"}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={()=>setExpanded(x=>({...x,[c.meeting_id]:!isOpen}))} data-testid={`conf-toggle-${c.meeting_id}`}>
                  {isOpen ? <ChevronDown className="w-4 h-4 mr-1"/> : <ChevronRight className="w-4 h-4 mr-1"/>} Utenti
                </Button>
                <Button size="sm" onClick={()=>endOne(c.meeting_id)} disabled={ending === c.meeting_id} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`conf-end-${c.meeting_id}`}><XCircle className="w-4 h-4 mr-1"/>Chiudi</Button>
              </div>
            </div>
            {isOpen && (
              <div className="mt-4 grid md:grid-cols-2 gap-4 border-t border-[#E2E8F0] pt-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#1F4E79] mb-2">Moderatori ({mods.length})</div>
                  {mods.length ? mods.map((a,i)=><div key={`m-${i}`} className="text-sm text-stone-700 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-[#1F4E79]"/> {a.nome}</div>) : <div className="text-sm text-stone-400">Nessuno</div>}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#C96F0E] mb-2">Studenti / Partecipanti ({studs.length})</div>
                  {studs.length ? studs.map((a,i)=><div key={`s-${i}`} className="text-sm text-stone-700 flex items-center gap-1"><Users className="w-3.5 h-3.5"/> {a.nome}</div>) : <div className="text-sm text-stone-400">Nessuno</div>}
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
